import React from 'react'
import { InferType } from 'yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import { captureException } from '@sentry/react'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { societyDocumentUpdateSchema } from '../../../../forms/schemas/society_document_update'
import { societyDocumentUploadSchema } from '../../../../forms/schemas/society_document_upload'
import { IDocumentModel } from '../../../../interfaces/models/documents.interfaces'
import {
  binderQueries,
  createBinderDocument,
  createBinderForUnit,
  patchBinderDocument,
} from '../../../../api/binder'
import { NBinder } from '../../../../interfaces/services/binder.interfaces'
import { UpdateDocumentForm } from '../../Documents/CreateUpdateDocumentForm/UpdateDocumentForm'
import { CreateDocumentForm } from '../../Documents/CreateUpdateDocumentForm/CreateDocumentForm'
import { BinderType } from '../../../../views/society/Management/BinderPickerView/helper'

interface CreateUpdateBinderDocumentModalProps {
  show: boolean
  binderId: string | string[]
  close: () => void
  parentId?: string
  children?: React.ReactNode
  id?: string
  unitId?: string
  isBoardDocuments?: boolean
  rows?: BinderType[]
  published?: boolean
}

export const CreateUpdateBinderDocumentModal = ({
  id,
  binderId,
  unitId,
  parentId,
  children,
  show,
  close,
  isBoardDocuments = false,
  rows,
  published,
}: CreateUpdateBinderDocumentModalProps): React.ReactNode => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const societyId = society?._id || ''
  const { data: document, isLoading: isLoadingDocument } = useQuery(
    binderQueries.getDocument(society?._id, id)
  )
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!id
  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateDocument.flashMessage.updateDocumentFailure'
      : 'createUpdateDocument.flashMessage.createDocumentFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateDocument.flashMessage.updateDocumentSuccess'
      : 'createUpdateDocument.flashMessage.createDocumentSuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }
  const { mutate: mutatePatchDocument, isPending: isPatchingDocument } =
    useMutation({
      mutationFn: patchBinderDocument,
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const getSuccessMessage = (isSingular: boolean): string => {
    return `${translate(
      isSingular
        ? 'createUpdateDocument.flashMessage.createDocumentSingularSuccess'
        : 'createUpdateDocument.flashMessage.createDocumentPluralSuccess'
    )}`
  }

  const getFailureMessage = (isSingular: boolean): string => {
    return `${translate(
      isSingular
        ? 'createUpdateDocument.flashMessage.createDocumentSingularFailure'
        : 'createUpdateDocument.flashMessage.createDocumentPluralFailure'
    )}`
  }

  const showMessage = (type: ToastType, message: string): void => {
    setToastNotification(type, message)
  }

  const { mutate: mutateCreateDocuments, isPending: isCreatingDocuments } =
    useMutation({
      mutationFn: async (params: NBinder.NCreate.IRequestBody[]) => {
        const promises = params.map(async (body) => {
          return (await createBinderDocument(societyId, body)).data.data
        })

        return Promise.all(promises)
      },
      onSuccess: (response) => {
        const isOneFile = response.length === 1
        showMessage(ToastType.SUCCESS, getSuccessMessage(isOneFile))
        close()
      },
      onError: (_error, params) => {
        const isOneFile = params.length === 1
        showMessage(ToastType.DANGER, getFailureMessage(isOneFile))
      },
    })

  const onSubmitUpdateDocument = (
    data: InferType<typeof societyDocumentUpdateSchema>
  ): void => {
    id && mutatePatchDocument({ id, body: data })
  }

  const onSubmitCreateDocument = async (
    data: InferType<typeof societyDocumentUploadSchema>
  ): Promise<void> => {
    if (!data.files) {
      showMessage(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
      return
    }

    // When uploading to multiple binders at once (from BinderPickerView), we need to create/get the binder for each unit
    let binderIdsForRows: Array<{ binderId: string; unitId: string }> = []
    if (Array.isArray(binderId) && rows) {
      const binderPromises = rows.map(async (row) => {
        // If binder already exists, creatBinderForUnit just returns existing binder for that unit
        const binderResponse = await createBinderForUnit(societyId, row.unitId)
        const newBinderId = binderResponse.data.data?.id
        return newBinderId
          ? { binderId: newBinderId, unitId: row.unitId }
          : null
      })
      const results = await Promise.all(binderPromises)
      binderIdsForRows = results.filter(
        (result): result is { binderId: string; unitId: string } =>
          result !== null
      )
    }

    const files = data.files.flatMap((_file) => {
      if (Array.isArray(binderId)) {
        // When uploading to multiple binders at once (from BinderPickerView)
        return binderIdsForRows.map((binderInfo) => {
          const doc: NBinder.NCreate.IRequestBody = {
            data: _file.base64,
            name: _file.name,
            binderId: binderInfo.binderId,
            filename: _file.filename,
            parentId,
            societyId: society?._id,
            unitId: binderInfo.unitId,
            type: _file.filename.split('.').pop() as IDocumentModel['type'],
            notificationSettings:
              data.notificationSettings as NBinder.NCreate.IRequestBody['notificationSettings'],
          }
          return doc
        })
      }
      // When uploading inside a specific binder
      const doc: NBinder.NCreate.IRequestBody = {
        data: _file.base64,
        name: _file.name,
        binderId,
        filename: _file.filename,
        parentId,
        societyId: society?._id,
        unitId,
        type: _file.filename.split('.').pop() as IDocumentModel['type'],
        notificationSettings:
          data.notificationSettings as NBinder.NCreate.IRequestBody['notificationSettings'],
      }
      return doc
    })

    mutateCreateDocuments(files)
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const loading = isPatchingDocument || isCreatingDocuments

  if (isLoadingDocument) {
    return null
  }

  const notificationSettingsOptions: {
    value: 'none' | 'notification'
    label: string
  }[] = [
    {
      value: 'none',
      label: translate('common.notificationSettings.none'),
    },
    {
      value: 'notification',
      label: translate('common.notificationSettings.notification'),
    },
  ]

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate(
                updateMode
                  ? 'managementBinderView.editDocumentTitle'
                  : 'managementBinderView.addNewDocumentTitle'
              )}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              {updateMode && document ? (
                <UpdateDocumentForm
                  onError={onError}
                  onSubmit={onSubmitUpdateDocument}
                  onClose={close}
                  loading={loading}
                  document={document}
                />
              ) : (
                <CreateDocumentForm
                  onError={onError}
                  onSubmit={onSubmitCreateDocument}
                  onClose={close}
                  loading={loading}
                  isBoardDocuments={isBoardDocuments}
                  optionSettings={notificationSettingsOptions}
                  published={published}
                />
              )}
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
